import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HorizontalSidebar from "./horizontalSidebar";
import CollapsedSidebar from "./collapsedSidebar";
import { useSelector } from "react-redux";

import * as Icon from "react-feather";
import AllBranchTransfer from "../../components/adminsitration/AllBranchTransfer";

// Sidebar behaviour (same style as the TexasAlarms web app):
// - Section headings (DASHBOARDS, MASTERS, TRANSACTIONS, ...) fold open/closed
//   on click; the state is kept in localStorage. By default only DASHBOARDS
//   and the section holding the open page are open.
// - Hovering a folded section, or a module (Sales, Purchase, ...) that is not
//   open inline, shows its pages in a pop-up beside the sidebar.
// - In the icon rail (mini sidebar) every module shows its pages on hover.
// - The open page is highlighted, and its module starts open.
// The pop-up is portaled to <body>: the Scrollbars view is overflow:hidden and
// would clip it.

const COLLAPSED_STORAGE_KEY = "axoone-sidebar-collapsed-sections-v1";
const ALWAYS_OPEN = ["DASHBOARDS"];
const CLOSE_DELAY = 150;

const hasChildren = (node) => Array.isArray(node?.children) && node.children.length > 0;
const urlOf = (node) => (node?.url && node.url !== "#" ? node.url : "");

const collectUrls = (nodes, out = []) => {
  (nodes || []).forEach((n) => {
    if (urlOf(n)) out.push(urlOf(n));
    collectUrls(n?.children, out);
  });
  return out;
};

// only the longest (most specific) matching menu url is active
const findActiveUrl = (pathname, menu) => {
  const path = decodeURI(pathname || "").toLowerCase();
  const hits = collectUrls(menu).filter((u) => {
    const url = u.toLowerCase();
    return path === url || path.startsWith(`${url}/`);
  });
  hits.sort((a, b) => b.length - a.length);
  return hits[0] || "";
};

const holdsUrl = (node, url) => !!url && (urlOf(node) === url || (node?.children || []).some((c) => holdsUrl(c, url)));

const loadCollapsed = (menu, activeUrl) => {
  try {
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {
    // fall through to the default
  }
  return new Set(
    (menu || [])
      .filter((s) => !ALWAYS_OPEN.includes(s?.title) && !holdsUrl(s, activeUrl))
      .map((s) => s?.title)
  );
};

const saveCollapsed = (set) => {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // private window / blocked storage: the state just isn't kept
  }
};

// section icons in the menu table are either a react-feather name or a
// font-awesome class ("fa fa-cubes")
const SectionIcon = ({ name }) => {
  const Feather = name && Icon[name];
  if (Feather) return <Feather size={16} className="sidebar-section-icon" />;
  return name ? <i className={`${name} sidebar-section-icon`} aria-hidden="true" /> : null;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { MenuServicelist } = useSelector((state) => state.menuservice);
  const menu = useMemo(() => (Array.isArray(MenuServicelist) ? MenuServicelist : []), [MenuServicelist]);
  const activeUrl = useMemo(() => findActiveUrl(location.pathname, menu), [location.pathname, menu]);

  const [subOpen, setSubopen] = useState("");
  const [subsidebar, setSubsidebar] = useState("");
  const [branchModal, setbranchModal] = useState(false);
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(menu, activeUrl));
  const collapsedReady = useRef(menu.length > 0);

  // the menu arrives after the first render: work out the default then
  useEffect(() => {
    if (!collapsedReady.current && menu.length > 0) {
      collapsedReady.current = true;
      setCollapsed(loadCollapsed(menu, activeUrl));
    }
  }, [menu, activeUrl]);

  // the module holding the open page starts open
  useEffect(() => {
    if (!activeUrl) return;
    for (const section of menu) {
      const module = (section?.children || []).find((m) => hasChildren(m) && holdsUrl(m, activeUrl));
      if (module) {
        setSubopen(module.title);
        const sub = (module.children || []).find((c) => hasChildren(c) && holdsUrl(c, activeUrl));
        if (sub) setSubsidebar(sub.title);
        return;
      }
    }
  }, [activeUrl, menu]);

  /* ------------------------------------------------------------ pop-up */
  // { title, items: [{ node, groupLabel? }], top, left }
  const [flyout, setFlyout] = useState(null);
  const flyoutRef = useRef(null);
  const closeTimer = useRef(null);

  const isMiniSidebar = () =>
    document.body.classList.contains("mini-sidebar") && !document.body.classList.contains("expand-menu");

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const closeFlyout = () => {
    cancelClose();
    setFlyout(null);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setFlyout(null), CLOSE_DELAY);
  };
  useEffect(() => cancelClose, []);

  const showFlyout = (title, items, anchor) => {
    cancelClose();
    const rect = anchor.getBoundingClientRect();
    const sidebarRect = document.getElementById("sidebar")?.getBoundingClientRect();
    setFlyout({ title, items, top: rect.top, left: (sidebarRect ? sidebarRect.right : rect.right) + 6 });
  };

  // a module (Sales, ...): in the icon rail always; in the full sidebar only
  // while it is not open inline
  const onModuleEnter = (module, e) => {
    if (!hasChildren(module)) return;
    if (!isMiniSidebar() && subOpen === module.title) return;
    showFlyout(module.title, module.children.map((node) => ({ node })), e.currentTarget);
  };

  // a folded section in the full sidebar: its modules with their pages
  const onSectionEnter = (section, e) => {
    if (isMiniSidebar() || !collapsed.has(section.title)) return;
    const items = [];
    (section.children || []).forEach((module) => {
      if (hasChildren(module)) {
        items.push({ groupLabel: module.title });
        module.children.forEach((node) => items.push({ node }));
      } else {
        items.push({ node: module });
      }
    });
    if (items.length) showFlyout(section.title, items, e.currentTarget);
  };

  // keep the pop-up inside the viewport once its height is known
  useEffect(() => {
    const el = flyoutRef.current;
    if (!el || !flyout) return;
    const overflow = el.getBoundingClientRect().bottom - (window.innerHeight - 12);
    if (overflow > 0) el.style.top = `${Math.max(12, flyout.top - overflow)}px`;
  }, [flyout]);

  // close on Escape, resize, or when the sidebar is toggled
  useEffect(() => {
    if (!flyout) return;
    const onKey = (e) => e.key === "Escape" && closeFlyout();
    const observer = new MutationObserver(closeFlyout);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", closeFlyout);
    return () => {
      observer.disconnect();
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", closeFlyout);
    };
  }, [flyout]);

  /* ------------------------------------------------------------ inline menu */
  const toggleSection = (title) => {
    closeFlyout();
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      saveCollapsed(next);
      return next;
    });
  };

  const toggleSidebar = (title, url, child, click) => {
    if (click == "Branch Transfer") {
      setbranchModal(true);
    }
    if (child?.length > 0) {
      closeFlyout();
      setSubopen(title == subOpen ? "" : title);
    } else if (url) {
      navigate(url);
    }
  };

  const toggleSubsidebar = (subitem) => {
    setSubsidebar(subitem == subsidebar ? "" : subitem);
  };

  const isActive = (node) => !!activeUrl && urlOf(node) === activeUrl;

  const renderFlyoutNode = (node, key) => {
    const ItemIcon = Icon[node?.icon];
    return (
      <li key={key}>
        <Link
          to={urlOf(node) || "#"}
          role="menuitem"
          className={isActive(node) ? "active" : ""}
          onClick={(e) => {
            if (node?.onClick == "Branch Transfer") {
              e.preventDefault();
              setbranchModal(true);
            }
            closeFlyout();
          }}
        >
          {ItemIcon ? <ItemIcon /> : <span className="sidebar-flyout-dot" />}
          <span>{node?.title}</span>
        </Link>
        {hasChildren(node) && (
          <ul className="sidebar-flyout-sub">
            {node.children.map((sub, j) => (
              <li key={j}>
                <Link to={urlOf(sub) || "#"} role="menuitem" className={isActive(sub) ? "active" : ""} onClick={closeFlyout}>
                  <span>{sub?.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div>
      <div className="sidebar" id="sidebar">
        <Scrollbars onScroll={() => flyout && closeFlyout()}>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                {menu.map((section, index) => {
                  const isCollapsed = collapsed.has(section?.title);
                  return (
                    <li
                      className={`submenu-open sidebar-section${isCollapsed ? " section-collapsed" : ""}`}
                      key={index}
                      onMouseEnter={(e) => onSectionEnter(section, e)}
                      onMouseLeave={scheduleClose}
                    >
                      <h6
                        className="submenu-hdr sidebar-section-hdr"
                        role="button"
                        tabIndex={0}
                        title={section?.title}
                        onClick={() => toggleSection(section?.title)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSection(section?.title);
                          }
                        }}
                      >
                        <span className="sidebar-section-name">
                          <SectionIcon name={section?.icon} />
                          <span className="sidebar-section-label">{section?.title}</span>
                        </span>
                        {isCollapsed ? (
                          <Icon.ChevronRight size={14} className="sidebar-section-chevron" />
                        ) : (
                          <Icon.ChevronDown size={14} className="sidebar-section-chevron" />
                        )}
                      </h6>
                      <ul className="sidebar-section-items">
                        {section?.children?.map((title, i) => {
                          const IconComponent = Icon[title.icon];
                          const moduleActive = holdsUrl(title, activeUrl);
                          return (
                            <li
                              key={i}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                onModuleEnter(title, e);
                              }}
                              onMouseLeave={scheduleClose}
                            >
                              <Link
                                to={urlOf(title) || "#"}
                                title={title?.title}
                                onClick={(e) => {
                                  if (hasChildren(title) && isMiniSidebar()) {
                                    // the pop-up already shows on hover; a click keeps it open
                                    e.preventDefault();
                                    onModuleEnter(title, e);
                                    return;
                                  }
                                  if (hasChildren(title) || !urlOf(title)) e.preventDefault();
                                  toggleSidebar(title?.title, urlOf(title), title.children, title.onClick);
                                }}
                                className={`${subOpen === title?.title ? "subdrop" : ""} ${moduleActive ? "active" : ""}`}
                              >
                                {IconComponent && <IconComponent />}
                                <span className="custom-active-span">{title?.title}</span>
                                {hasChildren(title) && <span className="menu-arrow" />}
                              </Link>
                              <ul style={{ display: subOpen === title?.title ? "block" : "none" }}>
                                {title?.children?.map((item, titleIndex) => {
                                  const IconComponent1 = Icon[item.icon];
                                  return (
                                    <li className="submenu submenu-two" key={titleIndex}>
                                      <Link
                                        to={urlOf(item) || "#"}
                                        className={`${isActive(item) || (hasChildren(item) && holdsUrl(item, activeUrl)) ? "active " : ""}ml-3`}
                                        onClick={(e) => {
                                          if (hasChildren(item)) {
                                            e.preventDefault();
                                            toggleSubsidebar(item?.title);
                                          }
                                          if (item?.onClick == "Branch Transfer") {
                                            e.preventDefault();
                                            setbranchModal(true);
                                          }
                                        }}
                                      >
                                        {IconComponent1 && <IconComponent1 />}
                                        {item?.title}
                                        {hasChildren(item) && <span className="menu-arrow inside-submenu" />}
                                      </Link>
                                      <ul style={{ display: subsidebar === item?.title ? "block" : "none" }}>
                                        {item?.children?.map((items, subIndex) => (
                                          <li key={subIndex}>
                                            <Link to={urlOf(items) || "#"} className={isActive(items) ? "active" : ""}>
                                              {items?.title}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
      {flyout &&
        createPortal(
          <div
            ref={flyoutRef}
            className="sidebar-flyout"
            role="menu"
            style={{ top: flyout.top, left: flyout.left }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className="sidebar-flyout-hdr">{flyout.title}</div>
            <ul>
              {flyout.items.map((entry, i) =>
                entry.groupLabel ? (
                  <li key={`g${i}`} className="sidebar-flyout-group">
                    {entry.groupLabel}
                  </li>
                ) : (
                  renderFlyoutNode(entry.node, i)
                )
              )}
            </ul>
          </div>,
          document.body
        )}
      <HorizontalSidebar />
      <CollapsedSidebar />
      {branchModal && <AllBranchTransfer handleClose={() => setbranchModal(false)} />}
    </div>
  );
};

export default Sidebar;
